import * as fs from 'node:fs';
import * as path from 'node:path';
import { spawnSync } from 'node:child_process';

const GIT_DIAGNOSTIC_MAX_BYTES = 10 * 1024 * 1024;

interface PreparePullRequestDiffOptions {
  temporaryDirectory: string;
  outputFile: string;
  remoteUrl: string;
  prNumber: number;
  baseSha: string;
  headSha: string;
  mergeBaseSha: string;
  githubToken: string;
  maxDiffBytes: number;
  maxGitRepositoryBytes: number;
}

export interface PreparedPullRequestDiff {
  mergeBaseSha: string;
  diffBytes: number;
  gitRepositoryBytes: number;
}

export function preparePullRequestDiff(options: PreparePullRequestDiffOptions): PreparedPullRequestDiff {
  validateOptions(options);
  const temporaryDirectory = fs.realpathSync(options.temporaryDirectory);
  const operationDirectory = fs.mkdtempSync(path.join(temporaryDirectory, 'pull-request-git-'));
  const repository = path.join(operationDirectory, 'repository.git');
  const askPassFile = path.join(operationDirectory, 'askpass.sh');
  let completed = false;

  try {
    fs.chmodSync(operationDirectory, 0o700);
    writeAskPass(askPassFile);
    const environment = safeGitEnvironment({
      askPassFile,
      githubToken: options.githubToken,
    });
    initializePartialRepository({
      repository,
      remoteUrl: options.remoteUrl,
      environment,
    });
    assertRepositoryWithinBudget(repository, options.maxGitRepositoryBytes);
    fetchExactCommits({
      repository,
      baseSha: options.baseSha,
      mergeBaseSha: options.mergeBaseSha,
      prNumber: options.prNumber,
      askPassFile,
      environment,
    });
    assertFetchedCommits({
      repository,
      expectedBaseSha: options.baseSha,
      expectedHeadSha: options.headSha,
      expectedMergeBaseSha: options.mergeBaseSha,
      environment,
    });
    assertRepositoryWithinBudget(repository, options.maxGitRepositoryBytes);

    const diffBytes = writeBoundedDiff({
      repository,
      outputFile: options.outputFile,
      baseSha: options.mergeBaseSha,
      headSha: options.headSha,
      maxDiffBytes: options.maxDiffBytes,
      environment,
    });
    const gitRepositoryBytes = assertRepositoryWithinBudget(repository, options.maxGitRepositoryBytes);
    completed = true;
    return { mergeBaseSha: options.mergeBaseSha, diffBytes, gitRepositoryBytes };
  } finally {
    try {
      fs.rmSync(operationDirectory, { recursive: true, force: true, maxRetries: 3, retryDelay: 200 });
    } catch (error) {
      if (completed) {
        throw error;
      }
    }
  }
}

function validateOptions(options: PreparePullRequestDiffOptions): void {
  assertCommitSha(options.baseSha, 'base SHA');
  assertCommitSha(options.headSha, 'head SHA');
  assertCommitSha(options.mergeBaseSha, 'merge base SHA');
  if (!Number.isInteger(options.prNumber) || options.prNumber < 1) {
    throw new Error(`Invalid pull request number: ${options.prNumber}.`);
  }
  if (options.remoteUrl.trim() === '' || options.remoteUrl.startsWith('-')) {
    throw new Error('Could not resolve a safe base repository clone URL.');
  }
  if (options.githubToken === '') {
    throw new Error('GitHub token is required to fetch the pull request head.');
  }
  assertPositiveSafeInteger(options.maxDiffBytes, 'max-diff-bytes');
  assertPositiveSafeInteger(options.maxGitRepositoryBytes, 'max-git-repository-bytes');
  const temporaryEntry = fs.statSync(options.temporaryDirectory);
  if (!temporaryEntry.isDirectory()) {
    throw new Error(`Git temporary path is not a directory: ${options.temporaryDirectory}`);
  }
}

function writeAskPass(askPassFile: string): void {
  fs.writeFileSync(askPassFile, [
    '#!/bin/sh',
    'case "$1" in',
    '  *Username*) printf \'%s\\n\' \'x-access-token\' ;;',
    '  *Password*) printf \'%s\\n\' "$CODEX_REVIEW_GIT_PASSWORD" ;;',
    '  *) exit 1 ;;',
    'esac',
    '',
  ].join('\n'), { mode: 0o700, flag: 'wx' });
}

function initializePartialRepository({
  repository,
  remoteUrl,
  environment,
}: {
  repository: string;
  remoteUrl: string;
  environment: NodeJS.ProcessEnv;
}): void {
  runGit(['init', '--bare', repository], {
    environment,
    operation: 'initialize temporary bare repository',
  });
  runGit(['-C', repository, 'config', 'gc.auto', '0'], {
    environment,
    operation: 'disable automatic Git maintenance',
  });
  runGit(['-C', repository, 'remote', 'add', 'origin', remoteUrl], {
    environment,
    operation: 'configure base repository remote',
  });
  runGit(['-C', repository, 'config', 'remote.origin.promisor', 'true'], {
    environment,
    operation: 'configure partial-clone promisor remote',
  });
  runGit(['-C', repository, 'config', 'remote.origin.partialclonefilter', 'blob:none'], {
    environment,
    operation: 'configure partial-clone filter',
  });
}

function fetchExactCommits({
  repository,
  baseSha,
  mergeBaseSha,
  prNumber,
  askPassFile,
  environment,
}: {
  repository: string;
  baseSha: string;
  mergeBaseSha: string;
  prNumber: number;
  askPassFile: string;
  environment: NodeJS.ProcessEnv;
}): void {
  runGit([
    '-c', 'credential.helper=',
    '-c', `core.askPass=${askPassFile}`,
    '-C', repository,
    'fetch',
    '--force',
    '--no-tags',
    '--no-recurse-submodules',
    '--no-auto-gc',
    '--filter=blob:none',
    '--depth=1',
    '--',
    'origin',
    `+${baseSha}:refs/codex-review/base`,
    `+refs/pull/${prNumber}/head:refs/codex-review/head`,
    `+${mergeBaseSha}:refs/codex-review/merge-base`,
  ], {
    environment,
    operation: 'fetch exact pull request base, head, and server-resolved merge base',
  });
}

function assertFetchedCommits({
  repository,
  expectedBaseSha,
  expectedHeadSha,
  expectedMergeBaseSha,
  environment,
}: {
  repository: string;
  expectedBaseSha: string;
  expectedHeadSha: string;
  expectedMergeBaseSha: string;
  environment: NodeJS.ProcessEnv;
}): void {
  const fetchedBaseSha = resolveCommit(repository, 'refs/codex-review/base', environment, 'fetched base branch');
  if (fetchedBaseSha !== expectedBaseSha) {
    throw new Error(`Fetched pull request base mismatch: expected ${expectedBaseSha}, received ${fetchedBaseSha}.`);
  }
  const fetchedHeadSha = resolveCommit(repository, 'refs/codex-review/head', environment, 'fetched pull request head');
  if (fetchedHeadSha !== expectedHeadSha) {
    throw new Error(`Fetched pull request head mismatch: expected ${expectedHeadSha}, received ${fetchedHeadSha}.`);
  }
  const fetchedMergeBaseSha = resolveCommit(repository, 'refs/codex-review/merge-base', environment, 'fetched merge base');
  if (fetchedMergeBaseSha !== expectedMergeBaseSha) {
    throw new Error(`Fetched pull request merge-base mismatch: expected ${expectedMergeBaseSha}, received ${fetchedMergeBaseSha}.`);
  }
}

function resolveCommit(
  repository: string,
  ref: string,
  environment: NodeJS.ProcessEnv,
  description: string,
): string {
  return captureGit([
    '-C', repository,
    'rev-parse',
    '--verify',
    `${ref}^{commit}`,
  ], {
    environment,
    operation: `resolve ${description}`,
  }).trim();
}

function writeBoundedDiff({
  repository,
  outputFile,
  baseSha,
  headSha,
  maxDiffBytes,
  environment,
}: {
  repository: string;
  outputFile: string;
  baseSha: string;
  headSha: string;
  maxDiffBytes: number;
  environment: NodeJS.ProcessEnv;
}): number {
  const result = spawnSync('git', [
    '--no-pager',
    '-C', repository,
    'diff',
    '--full-index',
    '--no-ext-diff',
    '--no-textconv',
    '--no-renames',
    baseSha,
    headSha,
    '--',
  ], {
    env: environment,
    stdio: ['ignore', 'pipe', 'pipe'],
    maxBuffer: maxDiffBytes,
  });
  if (result.error && (result.error as NodeJS.ErrnoException).code === 'ENOBUFS') {
    throw new Error(`Pull request diff exceeds max-diff-bytes (${maxDiffBytes}); refusing to stage an unbounded diff.`);
  }
  assertGitSuccess(result, 'generate pull request diff');
  const output = Buffer.isBuffer(result.stdout)
    ? result.stdout
    : Buffer.from(result.stdout || '');
  if (output.length > maxDiffBytes) {
    throw new Error(`Pull request diff exceeds max-diff-bytes (${maxDiffBytes}); refusing to stage an unbounded diff.`);
  }

  fs.mkdirSync(path.dirname(outputFile), { recursive: true });
  const temporaryOutput = path.join(
    path.dirname(outputFile),
    `.${path.basename(outputFile)}-${process.pid}-${Date.now()}.tmp`,
  );
  try {
    fs.writeFileSync(temporaryOutput, output, { mode: 0o600, flag: 'wx' });
    fs.renameSync(temporaryOutput, outputFile);
  } finally {
    fs.rmSync(temporaryOutput, { force: true });
  }
  return output.length;
}

function assertRepositoryWithinBudget(repository: string, maxBytes: number): number {
  const size = directorySizeAtMost(repository, maxBytes);
  if (size > maxBytes) {
    throw new Error(`Temporary Git repository exceeds max-git-repository-bytes (${maxBytes}); refusing to retain unbounded Git objects.`);
  }
  return size;
}

function directorySizeAtMost(root: string, maxBytes: number): number {
  let total = 0;
  const pending = [root];
  while (pending.length > 0) {
    const current = pending.pop() as string;
    for (const name of fs.readdirSync(current)) {
      const target = path.join(current, name);
      const entry = fs.lstatSync(target);
      if (entry.isDirectory() && !entry.isSymbolicLink()) {
        pending.push(target);
      } else {
        total += entry.size;
        if (total > maxBytes) {
          return total;
        }
      }
    }
  }
  return total;
}

function assertCommitSha(value: string, description: string): void {
  if (!/^[0-9a-f]{40}$/i.test(value)) {
    throw new Error(`Invalid ${description}: ${value}.`);
  }
}

function assertPositiveSafeInteger(value: number, description: string): void {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error(`${description} must be a positive integer`);
  }
}

function safeGitEnvironment({
  askPassFile,
  githubToken,
}: {
  askPassFile: string;
  githubToken: string;
}): NodeJS.ProcessEnv {
  const environment: NodeJS.ProcessEnv = {
    ...process.env,
    CODEX_REVIEW_GIT_PASSWORD: githubToken,
    GIT_ASKPASS: askPassFile,
    GIT_TERMINAL_PROMPT: '0',
    GIT_PAGER: 'cat',
  };
  for (const name of [
    'GIT_EXTERNAL_DIFF',
    'GIT_TRACE',
    'GIT_TRACE_CURL',
    'GIT_TRACE_PACKET',
    'GIT_TRACE_PERFORMANCE',
    'GIT_TRACE_SETUP',
    'GIT_CURL_VERBOSE',
    'GIT_DIR',
    'GIT_WORK_TREE',
    'GIT_COMMON_DIR',
    'GIT_OBJECT_DIRECTORY',
    'GIT_ALTERNATE_OBJECT_DIRECTORIES',
    'GIT_INDEX_FILE',
    'GIT_NAMESPACE',
  ]) {
    delete environment[name];
  }
  return environment;
}

function runGit(
  args: string[],
  {
    environment,
    operation,
  }: {
    environment: NodeJS.ProcessEnv;
    operation: string;
  },
): void {
  const result = spawnSync('git', args, {
    encoding: 'utf8',
    env: environment,
    stdio: ['ignore', 'pipe', 'pipe'],
    maxBuffer: GIT_DIAGNOSTIC_MAX_BYTES,
  });
  assertGitSuccess(result, operation);
}

function captureGit(
  args: string[],
  {
    environment,
    operation,
  }: {
    environment: NodeJS.ProcessEnv;
    operation: string;
  },
): string {
  const result = spawnSync('git', args, {
    encoding: 'utf8',
    env: environment,
    stdio: ['ignore', 'pipe', 'pipe'],
    maxBuffer: GIT_DIAGNOSTIC_MAX_BYTES,
  });
  assertGitSuccess(result, operation);
  return String(result.stdout || '');
}

function assertGitSuccess(
  result: ReturnType<typeof spawnSync>,
  operation: string,
): void {
  if (result.error) {
    throw new Error(`Could not ${operation}: ${result.error.message}`);
  }
  if (result.status !== 0) {
    throw new Error(`Could not ${operation} (git exit ${result.status ?? 'unknown'}): ${singleLine(diagnosticText(result.stderr))}`);
  }
}

function diagnosticText(value: Buffer | string | null): string {
  if (Buffer.isBuffer(value)) {
    return value.toString('utf8');
  }
  return String(value || '');
}

function singleLine(value: string): string {
  return value.replace(/\s+/g, ' ').trim() || '(no diagnostics)';
}
